/**
 * Devotee accounts — the most privacy-sensitive screen in this console.
 *
 * **Masked by default, and the mask is the product.** The list shows a phone's last four, an
 * email's domain and a first name. The profile shows *counts* — "three family members" — never
 * their names, gotras or birth places. An operator answering "did my booking go through?" needs to
 * confirm they have the right person and nothing else; making the whole profile readable so that
 * one field is convenient is how a support desk becomes the largest PII leak in the company.
 *
 * **A reveal is an act, not a view.** One field at a time, with a support case and a reason, and
 * the server writes the audit row before it answers. The dialog says so before the operator
 * commits, because somebody who does not know they are being recorded will eventually reveal
 * something they cannot justify.
 *
 * **There is no delete button, and there is not meant to be.** `support` holds R W D in the matrix
 * and D means suspend or anonymise. Suspension blocks sign-in and keeps every row; the only
 * removal path is the DPDP closure pipeline, which is its own workflow.
 *
 * **The header strip is counts and one ratio, from the server.** It is never derived from the
 * page of rows below it, which is 25 accounts of a search, not the base.
 */
import { useState } from 'react';
import { Chip, Note, StatusChip } from '../components/primitives';
import { DataTable, DetailPage, Pager } from '../components/data';
import { count } from '../lib/text';
import { KV } from '../components/entities';
import { ConfirmAction, FilterBar, ScopeAction, Section, StatTile } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { PhoneIntake } from './SupportDesk';
import {
  useRevealField,
  useSetSuspended,
  useUser,
  useUserAggregates,
  useUserConsents,
  useUsers,
  type ConsentEntry,
  type UserAggregates,
  type UserRow,
} from '../lib/api/hooks/users';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 25;

const TABS = [
  { key: '', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'closed', label: 'Closed' },
] as const;

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

const STATUS_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  active: 'success',
  suspended: 'error',
  closed: 'neutral',
};

export function userStatusTone(status: string): 'success' | 'warn' | 'error' | 'neutral' {
  return STATUS_TONE[status] ?? 'neutral';
}

/** What each reveal is for, in the words the confirm dialog shows. */
const FIELD_PURPOSE: Record<string, string> = {
  phone: 'the full phone number',
  email: 'the full email address',
  full_name: 'the account holder’s full name',
  gotra: 'the devotee’s gotra',
};

export function fieldPurpose(field: string): string {
  return FIELD_PURPOSE[field] ?? field.replace(/_/g, ' ');
}

/** Exported for its own test: a null rate means no active accounts, which is not 0%. */
export function optInRate(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

const SURFACE: Record<string, string> = {
  app: 'App',
  web: 'Website',
  admin: 'Support',
  system: 'Provider (bounce or spam report)',
};

/** `2026-05-02T12:44:00Z` → `2 May 2026`, in IST — the day the devotee acted. */
export function consentDay(at: string): string {
  return new Date(at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

/** One row per scope, from the latest ledger entry — granted is success, withdrawn is error. */
export function consentRows(current: readonly ConsentEntry[]) {
  return current.map((c) => {
    const granted = c.action === 'granted';
    return {
      id: c.consent_type,
      scope: c.consent_type.replace(/_/g, ' '),
      ver: c.policy_version,
      state: <StatusChip tone={granted ? 'success' : 'error'} label={`${granted ? 'Granted' : 'Withdrawn'} ${consentDay(c.occurred_at)}`} />,
      channel: SURFACE[c.source_surface] ?? c.source_surface,
    };
  });
}

/** The withdrawal propagation line: what the campaign engine now suppresses, and since when. */
export function withdrawalNotes(current: readonly ConsentEntry[]): string[] {
  return current
    .filter((c) => c.action === 'withdrawn')
    .map((c) => `${c.consent_type.replace(/_/g, ' ')} withdrawn ${consentDay(c.occurred_at)} — suppressed from campaigns since ${new Date(c.occurred_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}.`);
}

function ConsentPanel({ userId }: { userId: number }) {
  const consents = useUserConsents(userId);
  const current = consents.data?.current ?? [];
  return (
    <Section title="Consent">
      <Note>Read-only. Consent is the principal’s act — the console never grants or restores it on anyone’s behalf.</Note>
      {consents.isPending ? (
        <LoadingState rows={2} />
      ) : consents.isError ? (
        <ErrorState title="Could not read the consent ledger" cause={errorMessage(consents.error)} />
      ) : current.length === 0 ? (
        <p style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-cell)' }}>No consent recorded for this account.</p>
      ) : (
        <>
          <DataTable
            density="compact"
            columns={[
              { key: 'scope', label: 'Scope' },
              { key: 'ver', label: 'Document', mono: true },
              { key: 'state', label: 'State' },
              { key: 'channel', label: 'Captured on' },
            ]}
            rows={consentRows(current)}
          />
          {withdrawalNotes(current).map((note) => (
            <p key={note} style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>{note}</p>
          ))}
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
            {`${consents.data.history.length} ledger entries — append-only, kept through closure.`}
          </p>
        </>
      )}
    </Section>
  );
}

function AggregateStrip({ data }: { data: UserAggregates | undefined }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 'var(--sp-md)',
      }}
    >
      <StatTile label="Active" value={data?.accounts_by_status.active ?? '—'} note="can sign in" />
      <StatTile
        label="Suspended"
        value={data?.accounts_by_status.suspended ?? '—'}
        note="sign-in blocked, every row kept"
      />
      <StatTile label="New this week" value={data?.new_this_week ?? '—'} note="last 7 days, rolling" />
      <StatTile
        label="Closures pending"
        value={data?.closures_pending ?? '—'}
        note="DPDP requests not yet executed"
      />
      <StatTile
        label="Marketing opt-in"
        value={data ? optInRate(data.consent_opt_in_rate) : '—'}
        note="of active accounts"
      />
    </div>
  );
}

export function Users() {
  const { scope, go, toast } = useConsole();
  const [q, setUrlQ] = useUrlSearch('users');
  const [tab, setUrlTab] = useUrlTab('users', TAB_KEYS, '');
  const [page, setPage] = useState(1);
  /* A different search or status is a different list: page 2 of all accounts means nothing in one. */
  const setQ = (v: string) => {
    setUrlQ(v);
    setPage(1);
  };
  const setTab = (v: string) => {
    setUrlTab(v);
    setPage(1);
  };
  const [revealing, setRevealing] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string | null>>({});
  const [suspending, setSuspending] = useState(false);
  const [ticketing, setTicketing] = useState(false);

  const aggregates = useUserAggregates();
  const users = useUsers({ page, page_size: PAGE_SIZE, q: q.trim() || undefined, status: tab || undefined });
  const rows: UserRow[] = users.data?.items ?? [];
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: tab + q });

  const detail = useUser(open?.id);
  const reveal = useRevealField();
  const setSuspended = useSetSuspended();

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const revealDialog = open && revealing && (
    <ConfirmAction
      open
      title={`Reveal ${fieldPurpose(revealing)}?`}
      phrase="REVEAL"
      tone="warn"
      reason="Why this is needed (reaches the audit log)"
      consequences={[
        'This is written to the audit log with your name, the support case and your reason — before the value is shown to you.',
        'The log records which field was read. It never records the value, so this is not a second copy of the data.',
        'Reveal only the field you need. Each one is a separate, separately recorded decision.',
      ]}
      onCancel={() => setRevealing(null)}
      onConfirm={(reason: string) => {
        const field = revealing;
        setRevealing(null);
        /* The support case is the first token of the reason — the server demands both, and asking
           for them as two inputs in a confirm dialog that already asks for one is how operators
           start typing "n/a". */
        const [supportCase = '', ...rest] = reason.split(/\s+/);
        reveal.mutate(
          { id: open.id, field, supportCase, reason: rest.join(' ') || reason },
          {
            onSuccess: (r) => {
              setRevealed((prev) => ({ ...prev, [field]: r.value ?? null }));
              toast({ tone: 'info', message: `${fieldPurpose(field)} revealed · audited` });
            },
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          },
        );
      }}
      confirmLabel="Reveal and record"
    />
  );

  const suspendDialog = open && detail.data && (
    <ConfirmAction
      open={suspending}
      title={
        detail.data.user.status === 'suspended'
          ? 'Restore sign-in for this account?'
          : 'Block sign-in for this account?'
      }
      phrase={detail.data.user.status === 'suspended' ? 'RESTORE' : 'SUSPEND'}
      tone={detail.data.user.status === 'suspended' ? 'warn' : 'error'}
      reason
      consequences={
        detail.data.user.status === 'suspended'
          ? ['They can sign in again immediately. Nothing else changes.']
          : [
              'They cannot sign in. Every row they have is kept — this deletes nothing.',
              'Bookings already confirmed are NOT cancelled here. That is a separate decision, and collapsing the two would hide which one was actually made.',
            ]
      }
      onCancel={() => setSuspending(false)}
      onConfirm={(reason: string) => {
        const suspended = detail.data!.user.status !== 'suspended';
        setSuspending(false);
        setSuspended.mutate(
          { id: open.id, suspended, reason },
          outcome(suspended ? 'Sign-in blocked' : 'Sign-in restored', suspended ? 'info' : 'success'),
        );
      }}
      confirmLabel={detail.data.user.status === 'suspended' ? 'Restore' : 'Block sign-in'}
    />
  );

  if (open) {
    const d = detail.data;
    return (
      <DetailPage
        title={open.masked_name ?? open.masked_phone}
        code={open.masked_phone}
        status={<StatusChip tone={userStatusTone(open.status)} label={open.status} />}
        onClose={() => {
          setOpen(null);
          setTicketing(false);
          /* Revealed values are dropped when the profile closes. Holding them would mean the next
             operator to open this screen sees data nobody recorded them asking for. */
          setRevealed({});
        }}
        actions={
          <>
            {/* Export, correction and closure live in their own workspace: each needs a ticket, a
                channel and a reason, and none of them belongs one click from a profile read. */}
            <ScopeAction scope={scope} need="support" size="sm" variant="secondary" onClick={() => setTicketing(true)}>
              Open ticket
            </ScopeAction>
            <ScopeAction scope={scope} need="support" size="sm" variant="secondary" onClick={() => go('dsr', { userId: String(open.id) })}>
              Data requests
            </ScopeAction>
            <ScopeAction scope={scope} need="support" size="sm" onClick={() => setSuspending(true)}>
              {d?.user.status === 'suspended' ? 'Restore sign-in' : 'Block sign-in'}
            </ScopeAction>
          </>
        }
      >
        {/* The desk's own phone intake, with this account already bound as the caller. */}
        {ticketing && <PhoneIntake boundTo={open} onDone={() => setTicketing(false)} />}
        {detail.isPending && <LoadingState message="Reading the account…" />}
        {detail.isError && <ErrorState title="Could not read this account" cause={errorMessage(detail.error)} />}

        {d && (
          <>
            <Section title="Account">
              <KV
                rows={[
                  ['Phone', revealed.phone ?? d.user.masked_phone],
                  ['Email', revealed.email ?? d.user.masked_email ?? 'none'],
                  ['Name', revealed.full_name ?? d.user.masked_name ?? 'not given'],
                  ['Gotra', revealed.gotra ?? 'hidden'],
                  ['Phone verified', d.phone_verified ? 'Yes' : 'No'],
                  ['Email verified', d.email_verified ? 'Yes' : 'No'],
                  ['Sign-in methods', [d.has_password ? 'password' : null, ...d.social_logins].filter(Boolean).join(', ') || 'OTP only'],
                  ['Language', d.user.preferred_language],
                  ['Joined', formatDateTime(d.user.created_at)],
                ]}
              />
              <Note>
                Masked by default. Each reveal is one field, with a reason, recorded against your
                name before you see it.
              </Note>
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', marginTop: 'var(--sp-sm)' }}>
                {d.revealable_fields.map((field) => (
                  <ScopeAction
                    key={field}
                    scope={scope}
                    need="support"
                    size="sm"
                    variant="secondary"
                    disabled={revealed[field] !== undefined}
                    onClick={() => setRevealing(field)}
                  >
                    {revealed[field] !== undefined ? `${field} revealed` : `Reveal ${field.replace(/_/g, ' ')}`}
                  </ScopeAction>
                ))}
              </div>
            </Section>

            <Section title="What this account holds">
              <Note>
                Counts, not contents. A family record’s names, gotras and birth places are not
                support’s to browse.
              </Note>
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', marginTop: 'var(--sp-md)' }}>
                <Chip label={`${d.family_member_count} family members`} />
                <Chip label={`${d.address_count} saved addresses`} />
                <Chip label={`${d.active_session_count} active sessions`} />
              </div>
              <div style={{ marginTop: 'var(--sp-md)' }}>
                <KV
                  rows={[
                    [
                      'Marketing consent',
                      d.marketing_consent_at
                        ? `given ${formatDateTime(d.marketing_consent_at)} via ${d.marketing_consent_channel ?? 'unknown'}`
                        : 'not given',
                    ],
                  ]}
                />
              </div>
              {/* No delete control, and deliberately not a disabled one. Hard delete does not
                  exist as an API action; the only removal path is the DPDP closure pipeline,
                  which is its own workflow with a 30-day grace and per-class carve-outs. */}
              <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
                Deleting an account is not an action here. Closure runs through the DPDP pipeline —
                identity re-verified with the devotee, a 30-day grace in which signing in cancels
                it, then anonymisation by class with the legal carve-outs kept. Start one from Data
                requests above.
              </p>
            </Section>

            <ConsentPanel userId={d.user.id} />
          </>
        )}
        {revealDialog}
        {suspendDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <AggregateStrip data={aggregates.data} />
      {aggregates.isError && (
        <ErrorState title="Could not read the account counts" cause={errorMessage(aggregates.error)} />
      )}
      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="Phone or email — the number they just read out"
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        tab={tab}
        onTab={setTab}
      />

      {users.isPending && <LoadingState message="Searching…" />}
      {users.isError && <ErrorState title="Could not search accounts" cause={errorMessage(users.error)} />}
      {users.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="users"
          title="No account matches"
          body="Search matches a phone number or an email address. Nothing else is searchable — gotra and birth details are encrypted, which is the point."
        />
      )}
      {rows.length > 0 && (
        <DataTable
          columns={[
            { key: 'masked_name', label: 'Name' },
            { key: 'masked_phone', label: 'Phone' },
            { key: 'masked_email', label: 'Email' },
            { key: 'status', label: 'Status' },
          ]}
          rows={rows.map((u) => ({
            id: u.id,
            masked_name: u.masked_name ?? '—',
            masked_phone: u.masked_phone,
            masked_email: u.masked_email ?? '—',
            status: <StatusChip tone={userStatusTone(u.status)} label={u.status} />,
          }))}
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(r) => setOpen(rows.find((u) => u.id === r.id)!)}
          footer={`${count(users.data!.total, 'account')} · ${QUEUE_HINT}`}
        />
      )}
      {users.data && <Pager page={page} pageSize={users.data.page_size} total={users.data.total} onPage={setPage} />}
    </>
  );
}
